/**
 * FileSelector Component
 * Allows users to upload or select existing files with modern Tailwind CSS design
 */

import React, { useState, useEffect } from 'react';
import { JobFile } from '../../types/job';
import { Upload, AlertCircle, CheckCircle, FolderOpen, File } from 'lucide-react';

interface FileSelectorProps {
  onFileSelect: (fileIds: string[], files: JobFile[]) => void;
  onFileUpload: (file: File) => Promise<string>;
  acceptedFileTypes?: string;
  loading?: boolean;
  error?: string | null;
  selectedFileIds?: string[];
}

interface ExistingFile {
  key: string;
  size: number;
  uploaded: string;
  contentType: string;
}

export const FileSelector: React.FC<FileSelectorProps> = ({
  onFileSelect,
  onFileUpload,
  acceptedFileTypes = '*',
  loading = false,
  error = null,
  selectedFileIds = [],
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedExistingFiles, setSelectedExistingFiles] = useState<string[]>(selectedFileIds || []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'upload' | 'browse'>('upload');
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);

  // Fetch existing files when browse tab is opened
  useEffect(() => {
    if (activeTab === 'browse' && existingFiles.length === 0 && !loadingFiles) {
      fetchExistingFiles();
    }
  }, [activeTab]);

  const fetchExistingFiles = async () => {
    try {
      setLoadingFiles(true);
      setBrowseError(null);
      const response = await fetch('/api/files');
      
      if (!response.ok) {
        throw new Error('Failed to fetch files');
      }
      
      const data = await response.json();
      const files = data.files || data;
      setExistingFiles(Array.isArray(files) ? files : []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch files';
      setBrowseError(message);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadError(null);
      const fileId = await onFileUpload(selectedFile);

      const jobFile: JobFile = {
        id: fileId,
        name: selectedFile.name,
        size: selectedFile.size,
        type: selectedFile.type,
        uploadedAt: new Date().toISOString(),
      };

      // Add to existing selected files, don't replace them
      const newSelectedIds = [...selectedExistingFiles, fileId];
      const newJobFiles = selectedExistingFiles.map(id => {
        const existingFile = existingFiles.find(f => f.key === id);
        return {
          id,
          name: id.split('/').pop() || id,
          size: existingFile?.size || 0,
          type: existingFile?.contentType || 'application/octet-stream',
          uploadedAt: existingFile?.uploaded || new Date().toISOString(),
        };
      });

      onFileSelect([...newSelectedIds], [...newJobFiles, jobFile]);
      setSelectedFile(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload file';
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleSelectExistingFile = (file: ExistingFile) => {
    // Toggle file selection
    const newSelectedIds = selectedExistingFiles.includes(file.key)
      ? selectedExistingFiles.filter(id => id !== file.key)
      : [...selectedExistingFiles, file.key];
    
    setSelectedExistingFiles(newSelectedIds);

    // Convert to JobFiles and notify parent
    const jobFiles: JobFile[] = newSelectedIds.map(fileKey => {
      const existingFile = existingFiles.find(f => f.key === fileKey);
      return {
        id: fileKey,
        name: fileKey.split('/').pop() || fileKey,
        size: existingFile?.size || 0,
        type: existingFile?.contentType || 'application/octet-stream',
        uploadedAt: existingFile?.uploaded || new Date().toISOString(),
      };
    });

    onFileSelect(newSelectedIds, jobFiles);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 flex items-center gap-2 ${
            activeTab === 'upload'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Upload className="w-4 h-4" />
          Upload New File
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('browse')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 flex items-center gap-2 ${
            activeTab === 'browse'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <FolderOpen className="w-4 h-4" />
          Select Existing File
        </button>
      </div>

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
          <div className="text-center">
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">Upload a New File</h3>
            <p className="text-xs text-gray-600 mb-4">Choose a file from your computer to upload</p>

            <input
              type="file"
              accept={acceptedFileTypes}
              onChange={handleFileChange}
              disabled={uploading || loading}
              className="hidden"
              id="file-input"
            />

            <label
              htmlFor="file-input"
              className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors cursor-pointer disabled:opacity-50"
            >
              Choose File
            </label>
          </div>

          {/* Selected File Display */}
          {selectedFile && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-900">{selectedFile.name}</p>
                  <p className="text-xs text-green-700">{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Upload Button */}
          {selectedFile && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || uploading || loading}
              className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Uploading...
                </span>
              ) : (
                'Upload File'
              )}
            </button>
          )}

          {/* Errors */}
          {(uploadError || error) && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{uploadError || error}</p>
            </div>
          )}
        </div>
      )}

      {/* Browse Tab */}
      {activeTab === 'browse' && (
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="text-center mb-4">
            <FolderOpen className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <h3 className="text-sm font-medium text-gray-900">Select from Your Files</h3>
          </div>

          {/* Loading State */}
          {loadingFiles && (
            <div className="text-center py-8">
              <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-xs text-gray-600 mt-2">Loading files...</p>
            </div>
          )}

          {/* Error State */}
          {browseError && !loadingFiles && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{browseError}</p>
            </div>
          )}

          {/* Files List */}
          {!loadingFiles && existingFiles.length > 0 && (
            <div className="space-y-2">
              {existingFiles.map((file) => (
                <button
                  key={file.key}
                  type="button"
                  onClick={() => handleSelectExistingFile(file)}
                  className={`w-full p-3 border rounded-lg transition-colors text-left ${
                    selectedExistingFiles.includes(file.key)
                      ? 'bg-blue-50 border-blue-300 border-2'
                      : 'bg-gray-50 border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {selectedExistingFiles.includes(file.key) ? (
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    ) : (
                      <File className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.key.split('/').pop() || file.key}</p>
                      <p className="text-xs text-gray-600">
                        {formatFileSize(file.size)} • {formatDate(file.uploaded)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loadingFiles && existingFiles.length === 0 && !browseError && (
            <div className="text-center py-8">
              <FolderOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-4">No files found</p>
              <p className="text-xs text-gray-500">
                Upload files using the "Upload New File" tab to see them here
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
